import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Calendar, Search, Loader2, ChevronLeft, ChevronRight, ChevronDown, RefreshCw, Building, PanelLeft } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { getApiBaseUrl } from '../services/api';
import { authService } from '../services/authService';
import AppHeader from './AppHeader';

const PAGE_SIZES = [10, 25, 50, 100];

const SIDEBAR_MIN_WIDTH = 240;
const SIDEBAR_MAX_WIDTH = 520;
const SIDEBAR_DEFAULT_WIDTH = 320;

const STATUS_STYLES = {
  BOOKED: 'bg-blue-100 text-blue-800',
  CONFIRMED: 'bg-green-100 text-green-800',
  INITIATED: 'bg-yellow-100 text-yellow-800',
  RESCHEDULED: 'bg-purple-100 text-purple-800',
  CANCELED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-red-100 text-red-800',
  NONE: 'bg-gray-100 text-gray-800',
};

// Ztime sends more fields than AppointmentModel maps; they arrive flattened at
// the top level via the any-getter, so we probe several candidate key names.
// Keys support dot paths for nested objects (e.g. 'customerMaster.name').
const pick = (apt, keys) => {
  for (const key of keys) {
    const value = key
      .split('.')
      .reduce((obj, k) => (obj === null || obj === undefined ? obj : obj[k]), apt);
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }
  }
  return null;
};

const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  const str = String(value).trim();
  const parsed = new Date(str.includes('T') ? str : str.replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  const date = toDate(value);
  if (date) return date.toLocaleDateString();
  return value ? String(value) : '-';
};

const formatTime = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const str = String(value).trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?(\s?[AP]M)?$/i.test(str)) return str;
  const date = toDate(str);
  if (date) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return str;
};

const formatDateTime = (value) => {
  const date = toDate(value);
  if (!date) return value ? String(value) : '-';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

// The API splits date and time into separate fields (e.g. appt_date + appt_time,
// opened_date + open_time). Merge them into a single Date when both exist.
const combineDateAndTime = (dateValue, timeValue) => {
  const base = toDate(dateValue);
  if (!base) return null;
  if (timeValue === null || timeValue === undefined || timeValue === '') return base;
  const match = String(timeValue).match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return base;
  const combined = new Date(base);
  combined.setHours(Number(match[1]), Number(match[2]), Number(match[3] || 0), 0);
  return combined;
};

const resolveApptDateTime = (apt) => {
  const combined = combineDateAndTime(
    pick(apt, ['appt_date', 'appointmentDate', 'apptDate']),
    pick(apt, ['appt_time'])
  );
  if (combined) return combined;
  return pick(apt, ['appointmentTime', 'apptTime', 'appointmentDate', 'appt_time']);
};

const resolveCreatedDateTime = (apt) => {
  const combined = combineDateAndTime(
    pick(apt, ['opened_date', 'openedDate', 'created_date', 'createdDate']),
    pick(apt, ['open_time', 'openTime', 'created_time', 'createdTime'])
  );
  if (combined) return combined;
  return pick(apt, ['creationTime', 'createdDate', 'createdAt', 'createDate', 'creationDate', 'when_added', 'whenAdded']);
};

const resolvePromisedDate = (apt) =>
  pick(apt, ['promised_date', 'promisedDate', 'promiseDate', 'promise_date']);

const resolvePromisedTime = (apt) =>
  pick(apt, ['promised_time', 'promisedTime', 'promiseTime', 'promise_time']) ?? resolvePromisedDate(apt);

const resolveVehicle = (apt) => {
  const year = pick(apt, ['serviceVehicle.year', 'year', 'vehicleYear']);
  const make = pick(apt, ['serviceVehicle.make', 'make', 'vehicleMake']);
  const model = pick(apt, ['serviceVehicle.model', 'model', 'vehicleModel']);
  const label = [year, make, model].filter(Boolean).join(' ').trim();
  return label || null;
};

const resolveOpCodeItems = (apt) => {
  const items = pick(apt, ['lineitemdetails', 'lineItemDetails', 'services']);
  if (Array.isArray(items) && items.length > 0) {
    return items.map((s) => s?.opCode ?? s?.op_code ?? s?.opCodeDesc).filter(Boolean);
  }
  const direct = pick(apt, ['opCode', 'opcode', 'op_code', 'serviceOpCode']);
  return direct ? [direct] : [];
};

const resolveOpCode = (apt) => {
  const items = resolveOpCodeItems(apt);
  return items.length > 0 ? items.join(', ') : null;
};

const resolveServiceItems = (apt) => {
  const items = pick(apt, ['lineitemdetails', 'lineItemDetails', 'services']);
  if (Array.isArray(items) && items.length > 0) {
    return items
      .map((s) => s?.opCodeDesc ?? s?.labourOpDescription ?? s?.serviceDescription ?? s?.description)
      .filter(Boolean);
  }
  const direct = pick(apt, ['complaint_summary', 'complaintSummary', 'serviceDescription']);
  return direct ? [direct] : [];
};

const resolveServices = (apt) => {
  const items = resolveServiceItems(apt);
  return items.length > 0 ? items.join(', ') : null;
};

// Shows the first item truncated (full text on hover) plus a "+N" badge that
// reveals a popover listing every item - keeps wide cells from stretching the table.
const MultiItemCell = ({ items, mono = false }) => {
  if (!items || items.length === 0) return <span>-</span>;
  const [first, ...rest] = items;
  return (
    <div className="group relative flex items-center gap-1.5 max-w-[220px]">
      <span
        className={`truncate ${mono ? 'font-mono' : ''}`}
        title={items.join(', ')}
      >
        {first}
      </span>
      {rest.length > 0 && (
        <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-100">
          +{rest.length}
        </span>
      )}
      {rest.length > 0 && (
        <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-150 absolute left-0 top-full mt-1 z-20 w-72 rounded-lg border border-gray-200 bg-white p-2.5 shadow-lg">
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            {items.map((item, i) => (
              <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-purple-400 flex-shrink-0" />
                <span className="break-words">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const resolveCustomerName = (apt) => {
  const first = pick(apt, [
    'firstName',
    'customerFirstName',
    'first_name',
    'customerMaster.first_name',
    'customerMaster.firstName',
  ]);
  const last = pick(apt, [
    'lastName',
    'customerLastName',
    'last_name',
    'customerMaster.last_name',
    'customerMaster.lastName',
  ]);
  const combined = [first, last].filter(Boolean).join(' ').trim();
  if (combined) return combined;
  return pick(apt, ['cust_name', 'customerName', 'customerMaster.name', 'name']);
};

function AppointmentsV2({ user, onLogout, defaultEnterprise, defaultBOC, defaultStore }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);

  const [enterprises, setEnterprises] = useState([]);
  const [bocs, setBocs] = useState([]);
  const [stores, setStores] = useState([]);
  const [officeMappings, setOfficeMappings] = useState([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [selectedBOC, setSelectedBOC] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null);

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEnterprises, setLoadingEnterprises] = useState(false);
  const [loadingBOCs, setLoadingBOCs] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [error, setError] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });

  // Holds the user's saved preferences so the loaders can apply them as
  // defaults even when the props are null (e.g. page reloaded directly).
  const preferencesRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      // Load preferences first so they are available when the pickers apply defaults
      await loadPreferences();
      loadEnterprises();
      loadOfficeMappings();
    };
    init();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await authService.apiCall('/auth/preferences');
      if (response.ok) {
        preferencesRef.current = await response.json();
      }
    } catch (err) {
      console.error('Error loading user preferences:', err);
    }
  };

  const getDefaultEnterprise = () => defaultEnterprise ?? preferencesRef.current?.enterpriseIdFk ?? null;
  const getDefaultBOC = () => defaultBOC ?? preferencesRef.current?.bocIdFk ?? null;
  const getDefaultStore = () => defaultStore ?? preferencesRef.current?.storeIdFk ?? null;

  const loadEnterprises = async () => {
    setLoadingEnterprises(true);
    setError(null);
    try {
      const response = await authService.apiCall('/admin/enterprises');
      if (response.ok) {
        const data = await response.json();
        setEnterprises(data);
        const preferredEnterprise = getDefaultEnterprise();
        if (preferredEnterprise && data.find((e) => e.id === preferredEnterprise)) {
          setSelectedEnterprise(preferredEnterprise);
        }
      } else {
        setError('Failed to load enterprises');
      }
    } catch (err) {
      console.error('Error loading enterprises:', err);
      setError('Failed to load enterprises');
    } finally {
      setLoadingEnterprises(false);
    }
  };

  useEffect(() => {
    if (selectedEnterprise) {
      loadBOCs(selectedEnterprise);
    }
  }, [selectedEnterprise]);

  useEffect(() => {
    if (selectedBOC) {
      loadStores(selectedBOC);
    }
  }, [selectedBOC]);

  const loadBOCs = async (enterpriseId) => {
    setLoadingBOCs(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/bocs/enterprise/${enterpriseId}`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBocs(data);
        const preferredBOC = getDefaultBOC();
        if (preferredBOC && data.find((b) => b.id === preferredBOC)) {
          setSelectedBOC(preferredBOC);
        }
      }
    } catch (err) {
      console.error('Error loading BOCs:', err);
    } finally {
      setLoadingBOCs(false);
    }
  };

  const loadStores = async (bocId) => {
    setLoadingStores(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/stores/boc/${bocId}`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
        const preferredStore = getDefaultStore();
        if (preferredStore && data.find((s) => s.id === preferredStore)) {
          setSelectedStore(preferredStore);
        }
      }
    } catch (err) {
      console.error('Error loading stores:', err);
    } finally {
      setLoadingStores(false);
    }
  };

  const loadOfficeMappings = async () => {
    try {
      const response = await authService.apiCall('/admin/office-mapping');
      if (response.ok) {
        const data = await response.json();
        setOfficeMappings(data);
      }
    } catch (err) {
      console.error('Error loading office mappings:', err);
    }
  };

  const handleEnterpriseChange = async (enterpriseId) => {
    setSelectedEnterprise(enterpriseId);
    setSelectedBOC(null);
    setSelectedStore(null);
    setSelectedOffice(null);
    setBocs([]);
    setStores([]);
    setAppointments([]);
    if (enterpriseId) {
      await loadBOCs(enterpriseId);
    }
  };

  const handleBOCChange = async (bocId) => {
    setSelectedBOC(bocId);
    setSelectedStore(null);
    setSelectedOffice(null);
    setStores([]);
    setAppointments([]);
    if (bocId) {
      await loadStores(bocId);
    }
  };

  const handleStoreChange = (storeId) => {
    setSelectedStore(storeId);
    setSelectedOffice(null);
    setAppointments([]);
  };

  // Resolve the office mapping whenever a store is selected - covers both
  // manual selection and stores auto-selected from user preferences.
  useEffect(() => {
    if (!selectedStore || officeMappings.length === 0) return;
    const officeMapping = officeMappings.find((om) => om.storeIdFk === selectedStore);
    if (!officeMapping) {
      setSelectedOffice(null);
      setError('No office mapping found for this store');
      return;
    }
    if (!selectedOffice || selectedOffice.id !== officeMapping.id) {
      setSelectedOffice(officeMapping);
      loadAppointments(officeMapping, 0, pageSize, startDate, endDate);
    }
  }, [selectedStore, officeMappings, selectedOffice]);

  const loadAppointments = async (office, pageNum = page, size = pageSize, start = startDate, end = endDate) => {
    if (!office) {
      setError('Please select a store');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        officeMasterId: office.officeMasterId,
        size,
        page: pageNum,
      });
      if (start) params.set('startDate', start);
      if (end) params.set('endDate', end);
      const response = await fetch(
        `${getApiBaseUrl()}/appointments/office/v2?${params.toString()}`,
        { headers: authService.getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        const content = Array.isArray(data) ? data : data?.content;
        setAppointments(Array.isArray(content) ? content : []);
        setTotalElements(Number(data?.totalElements ?? (Array.isArray(content) ? content.length : 0)));
        setTotalPages(Number(data?.totalPages ?? 1));
        setPage(Number(data?.number ?? pageNum));
      } else {
        setAppointments([]);
        setTotalElements(0);
        setTotalPages(0);
        setError('Failed to load appointments');
      }
    } catch (err) {
      console.error('Error loading appointments:', err);
      setAppointments([]);
      setError('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    const newWidth = e.clientX;
    if (newWidth >= SIDEBAR_MIN_WIDTH && newWidth <= SIDEBAR_MAX_WIDTH) {
      setSidebarWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const statuses = useMemo(() => {
    const unique = new Set();
    appointments.forEach((apt) => {
      const status = pick(apt, ['appointmentStatus', 'status', 'apptStatus']);
      if (status) unique.add(String(status));
    });
    return [...unique].sort();
  }, [appointments]);

  const filtered = useMemo(() => {
    let rows = [...appointments];
    if (statusFilter !== 'ALL') {
      rows = rows.filter(
        (apt) => String(pick(apt, ['appointmentStatus', 'status', 'apptStatus']) || '') === statusFilter
      );
    }
    const term = keyword.trim().toLowerCase();
    if (term) {
      rows = rows.filter((apt) =>
        [
          pick(apt, ['appt_id', 'appointmentId', 'apptId', 'id']),
          resolveOpCode(apt),
          resolveServices(apt),
          resolveCustomerName(apt),
          resolveVehicle(apt),
          pick(apt, ['advisor_name', 'advisorName']),
          pick(apt, ['vin', 'vehicleVin', 'VIN', 'serviceVehicle.vin']),
          pick(apt, ['custno', 'custNo', 'customerNumber']),
          pick(apt, ['cellular', 'phone', 'phoneNumber', 'customerMaster.cellular']),
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term))
      );
    }
    rows.sort((a, b) => {
      const da = toDate(resolveApptDateTime(a));
      const db = toDate(resolveApptDateTime(b));
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da - db;
    });
    return rows;
  }, [appointments, keyword, statusFilter]);

  // Server-side pagination: `filtered` is the current page after client-side
  // keyword/status filtering.
  const effectiveTotalPages = Math.max(1, totalPages);
  const currentPage = Math.min(page, effectiveTotalPages - 1);
  const pageRows = filtered;

  const renderStatus = (apt) => {
    const status = pick(apt, ['appointmentStatus', 'status', 'apptStatus']);
    if (!status) return '-';
    const style = STATUS_STYLES[String(status).toUpperCase()] || 'bg-gray-100 text-gray-800';
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${style}`}>
        {String(status)}
      </span>
    );
  };

  const renderPagination = () => {
    if (!selectedOffice || (totalElements === 0 && !loading)) return null;
    const start = totalElements === 0 ? 0 : currentPage * pageSize + 1;
    const end = Math.min((currentPage + 1) * pageSize, totalElements);
    const filtering = keyword.trim() !== '' || statusFilter !== 'ALL';
    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-200">
        <div className="text-sm text-gray-600">
          Showing {start}-{end} of {totalElements}
          {filtering && ` (${filtered.length} match filters on this page)`}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value);
              setPageSize(newSize);
              if (selectedOffice) loadAppointments(selectedOffice, 0, newSize);
            }}
            className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>{size} / page</option>
            ))}
          </select>
          <button
            onClick={() => selectedOffice && loadAppointments(selectedOffice, currentPage - 1)}
            disabled={currentPage <= 0 || loading}
            className="p-1.5 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage + 1} of {effectiveTotalPages}
          </span>
          <button
            onClick={() => selectedOffice && loadAppointments(selectedOffice, currentPage + 1)}
            disabled={currentPage >= effectiveTotalPages - 1 || loading}
            className="p-1.5 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  if (user && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      className={`flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden ${
        isResizing ? 'cursor-col-resize select-none' : ''
      }`}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* TOP BAR */}
      <div className="flex items-center gap-3">
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            title="Open sidebar"
            aria-label="Open sidebar"
            aria-expanded={false}
            className="-ml-1 flex h-8 w-8 items-center justify-center rounded-md text-gray-700 transition-colors duration-150 hover:bg-gray-100/70 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300"
          >
            <PanelLeft size={17} strokeWidth={1.5} />
          </button>
        )}
        <AppHeader
          user={user}
          onLogout={onLogout}
          badgeCount={totalElements}
          badgeLabel="Appointments"
        />
      </div>

      {/* BODY */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* LEFT SIDEBAR - Store Selection */}
        <div
          className={`bg-white flex-shrink-0 overflow-y-auto overflow-x-hidden shadow-lg ${
            isResizing ? '' : 'transition-[width] duration-300 ease-in-out'
          }`}
          style={{ width: sidebarOpen ? sidebarWidth : 0 }}
        >
          <div className="p-4" style={{ width: sidebarWidth }}>
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-200 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800 flex items-center">
                  <Building className="h-4 w-4 mr-2 text-emerald-600" />
                  Store Selection
                </span>
                <button
                  onClick={() => setSidebarOpen(false)}
                  title="Close sidebar"
                  aria-label="Close sidebar"
                  className="-mr-1 flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors duration-150 hover:bg-white/70 hover:text-gray-600"
                >
                  <PanelLeft size={14} strokeWidth={1.5} />
                </button>
              </div>

              <div className="p-4 space-y-3">
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Enterprise</label>
                  <select
                    value={selectedEnterprise || ''}
                    onChange={(e) => handleEnterpriseChange(e.target.value ? Number(e.target.value) : null)}
                    disabled={loadingEnterprises}
                    className="block w-full pl-3 pr-10 py-2.5 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 rounded-lg shadow-sm appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  >
                    <option value="">Select Enterprise</option>
                    {enterprises.map((ent) => (
                      <option key={ent.id} value={ent.id}>
                        {ent.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-5">
                    <ChevronDown className="h-4 w-4" />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">BOC</label>
                  <select
                    value={selectedBOC || ''}
                    onChange={(e) => handleBOCChange(e.target.value ? Number(e.target.value) : null)}
                    disabled={!selectedEnterprise}
                    className="block w-full pl-3 pr-10 py-2.5 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 rounded-lg shadow-sm appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  >
                    <option value="">Select BOC</option>
                    {loadingBOCs ? (
                      <option disabled>Loading...</option>
                    ) : (
                      bocs.map((boc) => (
                        <option key={boc.id} value={boc.id}>
                          {boc.bocId}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-5">
                    {loadingBOCs ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500"></div>
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Store</label>
                  <select
                    value={selectedStore || ''}
                    onChange={(e) => handleStoreChange(e.target.value ? Number(e.target.value) : null)}
                    disabled={!selectedBOC}
                    className="block w-full pl-3 pr-10 py-2.5 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 rounded-lg shadow-sm appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  >
                    <option value="">Select Store</option>
                    {loadingStores ? (
                      <option disabled>Loading...</option>
                    ) : (
                      stores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-5">
                    {loadingStores ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500"></div>
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </div>

                {selectedOffice && (
                  <div className="mt-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-100">
                    <p className="text-xs font-medium text-gray-700 mb-1">Mapped Office</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedOffice.officeCode}</p>
                    <p className="text-xs text-gray-600">{selectedOffice.dealerCode}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RESIZE HANDLE */}
        {sidebarOpen && (
          <div
            className="w-1 bg-gray-200 hover:bg-emerald-400 cursor-col-resize flex-shrink-0 transition-colors"
            onMouseDown={handleMouseDown}
          />
        )}

        {/* MAIN CONTENT */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 py-4 border-b border-gray-200 flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="text-purple-600" size={20} />
              <h1 className="text-lg font-semibold text-gray-900">Appointments</h1>
            </div>
            <div className="flex flex-1 flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:justify-end">
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (selectedOffice) loadAppointments(selectedOffice, 0, pageSize, e.target.value, endDate);
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  if (selectedOffice) loadAppointments(selectedOffice, 0, pageSize, startDate, e.target.value);
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="Search ID, name, VIN, opcode..."
                  className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 w-full sm:w-64"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="ALL">All statuses</option>
                {statuses.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <button
                onClick={() => selectedOffice && loadAppointments(selectedOffice)}
                disabled={!selectedOffice || loading}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="mx-4 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Appt ID', 'Appt Time', 'OpCode', 'Services', 'Appt. Type', 'Status', 'Department', 'VIN', 'Vehicle', 'Customer Name', 'Advisor', 'Created', 'Promised Date', 'Promised Time'].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={14} className="px-4 py-12 text-center text-gray-500">
                      <Loader2 size={24} className="animate-spin inline-block mr-2" />
                      Loading appointments...
                    </td>
                  </tr>
                ) : !selectedOffice ? (
                  <tr>
                    <td colSpan={14} className="px-4 py-12 text-center text-gray-500">
                      Select a store to view appointments
                    </td>
                  </tr>
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="px-4 py-12 text-center text-gray-500">
                      No appointments found
                    </td>
                  </tr>
                ) : (
                  pageRows.map((apt, idx) => {
                    return (
                      <tr key={pick(apt, ['appt_id', 'appointmentId', 'apptId', 'id']) ?? idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {pick(apt, ['appt_id', 'appointmentId', 'apptId', 'id']) ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {formatDateTime(resolveApptDateTime(apt))}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <MultiItemCell items={resolveOpCodeItems(apt)} mono />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <MultiItemCell items={resolveServiceItems(apt)} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {pick(apt, ['appointment_type', 'appointmentType', 'apptType', 'type', 'transportType']) ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">{renderStatus(apt)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {pick(apt, ['department', 'dept', 'departmentCode', 'shopCode']) ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap font-mono">
                          {pick(apt, ['vin', 'vehicleVin', 'VIN', 'serviceVehicle.vin']) ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {resolveVehicle(apt) ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {resolveCustomerName(apt) ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {pick(apt, ['advisor_name', 'advisorName']) ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {formatDateTime(resolveCreatedDateTime(apt))}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {formatDate(resolvePromisedDate(apt))}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {formatTime(resolvePromisedTime(apt))}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {renderPagination()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppointmentsV2;
